import * as React from 'react';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import debounce from 'lodash/debounce';

import { IPaymentStore } from 'shared/entities/store/paymentStore';
import {
  convertStringToNumber,
  mapErrorCodeToMessage
} from 'shared/entities/common/utils';
import { IRootStore } from 'shared/entities/store/rootStore';
import { AnalyticsEvent } from 'shared/entities/analytics';
import { FieldModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';
import {
  ApplyPromoCodeError,
  AvailableMonthCount,
  availableMonthPayments,
  CalculatePayError,
  CalculatePayErrorCode,
  CheckPromoCodeError,
  CheckPromoCodeServer,
  InfoMessageType,
  IPaymentFill,
  mapAvailableMonthPaymentsToSale,
  mapCalculatePayErrorToMessage,
  normalizeCalculateResult,
  PaymentAddition,
  PaymentCalculateResult,
  PaymentParams,
  PaymentPromoServer
} from 'shared/entities/payment';
import { apiUrls } from 'shared/entities/domain';
import TypedTransComponent from 'shared/components/TypedTransComponent';
import { ReactionsHandlerStore } from 'stores/reactionsHandlerStore';
import { TariffKind } from 'shared/entities/tariff';

export const CALCULATE_LOADING_DEBOUNCE = 400;
export const DEBOUNCE_DELAY = 1000;
export const CHECK_PROMOCODE_DEBOUNCE_DELAY = 1500;
export const DEFAULT_MONTH_COUNT = availableMonthPayments[1].count;

export default class PaymentFillModel
  extends ReactionsHandlerStore
  implements IPaymentFill
{
  private _paymentStore: IPaymentStore;
  private _rootStore: IRootStore;
  private _infoMessage: InfoMessageType;

  readonly selectedMonthCount: FieldModel<number | null> = new FieldModel<
    number | null
  >(DEFAULT_MONTH_COUNT);
  readonly promoCode: FieldModel = new FieldModel<string>('');
  readonly amount: FieldModel = new FieldModel<string>('');
  readonly priceToPay: FieldModel<number | null> = new FieldModel<
    number | null
  >(null);
  readonly calculateResult: FieldModel<PaymentCalculateResult | null> =
    new FieldModel<PaymentCalculateResult | null>(null);
  readonly calculatingPriceStage: LoadingStageModel = new LoadingStageModel();
  readonly payStage: LoadingStageModel = new LoadingStageModel();
  readonly addition: FieldModel<PaymentAddition> =
    new FieldModel<PaymentAddition>(PaymentAddition.month);
  readonly checkPromoCodeStage: LoadingStageModel = new LoadingStageModel();
  readonly applyPromoCodeStage: LoadingStageModel = new LoadingStageModel();
  readonly promoCodeVerified: FieldModel<boolean> = new FieldModel<boolean>(
    false
  );

  readonly testPeriodPromoCodeCheckResult: FieldModel<CheckPromoCodeServer | null> =
    new FieldModel(null);

  constructor(paymentStore: IPaymentStore, rootStore: IRootStore) {
    super();
    this._paymentStore = paymentStore;
    this._rootStore = rootStore;

    makeObservable<PaymentFillModel, '_infoMessage'>(this, {
      _infoMessage: observable,

      numberAmount: computed,
      lessThanMinimalPayment: computed,
      infoMessage: computed,
      paymentDisabled: computed,
      paymentByInvoiceDisabled: computed,
      testPeriodPromoCodeSelected: computed,

      calculatePrice: action,
      setInfoMessage: action
    });
  }

  get infoMessage(): InfoMessageType {
    return this._infoMessage;
  }

  get numberAmount(): number {
    return convertStringToNumber(this.amount.value);
  }

  get lessThanMinimalPayment(): boolean {
    if (
      !this._paymentStore.info ||
      (this.infoMessage &&
        !this.infoMessage.isError &&
        !this._paymentStore.isCorporate)
    ) {
      return false;
    }

    return this.numberAmount < this._paymentStore.info.minimalPayment;
  }

  get paymentParams(): PaymentParams {
    const extraParams = {
      promocode: this.promoCode.value || undefined
    };

    if (
      this.addition.value === PaymentAddition.month &&
      this.selectedMonthCount.value !== null
    ) {
      return {
        ...extraParams,
        number_of_months: this.selectedMonthCount.value
      };
    }

    return {
      ...extraParams,
      amount: this.numberAmount
    };
  }

  get calculationForInvoice(): boolean {
    return this._rootStore.cabinetStore.documentsStore.creatingModalState
      .opened;
  }

  get paymentDisabled(): boolean {
    return this.paymentFormDisabled || !!this.infoMessage?.isError;
  }

  get paymentByInvoiceDisabled(): boolean {
    return (
      this.paymentFormDisabled ||
      this.testPeriodPromoCodeSelected ||
      (!!this.infoMessage &&
        this.infoMessage.isError &&
        this.infoMessage.code !== CalculatePayErrorCode.shouldPayAsLegalEntity)
    );
  }

  get paymentFormDisabled(): boolean {
    return (
      this.calculatingPriceStage.isLoading ||
      this.checkPromoCodeStage.isLoading ||
      this.applyPromoCodeStage.isLoading
    );
  }

  get paymentButtonIsLoading(): boolean {
    return this.testPeriodPromoCodeSelected
      ? this.applyPromoCodeStage.isLoading
      : this.payStage.isLoading;
  }

  get paymentInputDisabled(): boolean {
    return this.paymentFormDisabled || this.testPeriodPromoCodeSelected;
  }

  get selectedDiscount(): string | null {
    return this.selectedMonthCount.value
      ? mapAvailableMonthPaymentsToSale[this.selectedMonthCount.value] || null
      : null;
  }

  get testPeriodPromoCodeSelected(): boolean {
    return !!this.testPeriodPromoCodeCheckResult.value;
  }

  setInfoMessage = (value: InfoMessageType): void => {
    this._infoMessage = value;
  };

  calculatePrice = async (tariffKind?: TariffKind): Promise<void> => {
    if (
      this.calculatingPriceStage.isLoading ||
      this.testPeriodPromoCodeSelected
    ) {
      return;
    }

    this.calculatingPriceStage.loading();

    const response = await this._rootStore.networkStore.api<
      PaymentPromoServer,
      CalculatePayError
    >(apiUrls.PAYMENTS_CALCULATE, {
      method: 'POST',
      data: {
        tariff: tariffKind,
        ...this.paymentParams
      },
      errorsMap: mapCalculatePayErrorToMessage,
      showExpectedError: false
    });

    if (response.isError) {
      this.handleError(response.data?.code || null);
      setTimeout(() => {
        this.calculatingPriceStage.error();
      }, CALCULATE_LOADING_DEBOUNCE);
    } else {
      this.priceToPay.changeValue(response.data.price_to_pay);
      this.calculateResult.changeValue(normalizeCalculateResult(response.data));
      if (this.selectedMonthCount.value !== null) {
        this.amount.changeValue(String(response.data.price_to_pay));
      }
      if (this.promoCode.isError) {
        this.promoCode.resetError();
        this.promoCodeVerified.changeValue(true);
      }
      if (response.data.original_price === response.data.price_to_pay) {
        this.setInfoMessage(null);
      } else {
        const originalPrice = response.data.original_price;
        const priceToPay = response.data.price_to_pay;

        if (this.calculationForInvoice) {
          this.setInfoMessage({
            isError: false,
            data: (t) =>
              t('payment.successCalculate.forInvoice', {
                originalPrice,
                priceToPay,
                ns: 'entities',
                currency: this._paymentStore.currency
              })
          });
        } else {
          this.setInfoMessage({
            isError: false,
            data: (t) =>
              t('payment.successCalculate.forPayment', {
                originalPrice,
                priceToPay,
                ns: 'entities',
                currency: this._paymentStore.currency
              })
          });
        }
      }

      setTimeout(() => {
        this.calculatingPriceStage.success();
      }, CALCULATE_LOADING_DEBOUNCE);
    }
  };

  debouncedCalculatePrice = debounce(this.calculatePrice, DEBOUNCE_DELAY);

  checkPromoCode = async ({
    tariffKind,
    minimalPayment
  }: {
    tariffKind?: TariffKind;
    minimalPayment?: number;
  }): Promise<void> => {
    if (this.checkPromoCodeStage.isLoading || !this.promoCode.value) {
      return;
    }

    this.checkPromoCodeStage.loading();

    const response = await this._rootStore.networkStore.api<
      CheckPromoCodeServer,
      CheckPromoCodeError
    >(apiUrls.PROMOCODES_CHECK, {
      method: 'GET',
      data: {
        promocode: this.promoCode.value,
        tariff: tariffKind,
        number_of_months: this.selectedMonthCount.value ?? undefined
      },
      errorsMap: mapCalculatePayErrorToMessage,
      showExpectedError: false
    });

    if (response.isError) {
      this.handleError(response.data?.code || null);

      setTimeout(() => {
        this.checkPromoCodeStage.error();
      }, CALCULATE_LOADING_DEBOUNCE);
      return;
    }

    if (response.data.kind === 'discount') {
      this.validateInputValue({ tariffKind, minimalPayment });
      this.checkPromoCodeStage.success();
      return;
    }
    if (response.data.kind === 'test_period') {
      this.resetAmount();
      this.testPeriodPromoCodeCheckResult.changeValue(response.data);
      this.setInfoMessage({
        isError: false,
        data: (t) =>
          t('payment.successPromoCodeCheck.days', {
            ns: 'entities',
            count: response.data.value
          })
      });

      this.checkPromoCodeStage.success();
    }
  };

  debouncedCheckPromocode = debounce(
    this.checkPromoCode,
    CHECK_PROMOCODE_DEBOUNCE_DELAY
  );

  applyPromoCode = async (): Promise<BaseResponse> => {
    if (this.applyPromoCodeStage.isLoading || !this.promoCode.value) {
      return { isError: true };
    }

    this.applyPromoCodeStage.loading();

    const response = await this._rootStore.networkStore.api<
      {},
      ApplyPromoCodeError
    >(apiUrls.PROMOCODES_APPLY, {
      method: 'POST',
      data: { promocode: this.promoCode.value },
      errorsMap: mapCalculatePayErrorToMessage,
      showExpectedError: false
    });

    if (response.isError) {
      this.applyPromoCodeStage.error();
      this.handleError(response.data?.code || null);
      return { isError: true };
    }

    this.applyPromoCodeStage.success();

    this.setInfoMessage({
      isError: false,
      data: (t) =>
        t('payment.successPromoCodeApply', {
          ns: 'entities'
        })
    });
    this._paymentStore.getInfo();
    return {
      isError: false
    };
  };

  validateInputValue = ({
    minimalPayment,
    tariffKind
  }: {
    minimalPayment?: number;
    tariffKind?: TariffKind;
  }) => {
    if (!this._paymentStore.info) {
      return;
    }

    if (minimalPayment !== undefined) {
      if (this.numberAmount < minimalPayment) {
        this.setInfoMessage({
          isError: true,
          code: CalculatePayErrorCode.amountTooLow,
          data: (t) =>
            t('payment.minError', {
              ns: 'entities',
              minimalPayment,
              currency: this._paymentStore.currency
            })
        });
        return;
      }
    }

    if (this.lessThanMinimalPayment) {
      const { minimalPayment } = this._paymentStore.info;
      this.setInfoMessage({
        isError: true,
        code: CalculatePayErrorCode.amountTooLow,
        data: (t) =>
          t('payment.minError', {
            ns: 'entities',
            minimalPayment,
            currency: this._paymentStore.currency
          })
      });
    } else {
      this.calculatePrice(tariffKind);
    }
  };

  debouncedValidateInputValue = debounce(
    this.validateInputValue,
    DEBOUNCE_DELAY
  );

  selectMonthCount = (value: number | null, tariff?: TariffKind): void => {
    if (this.calculatingPriceStage.isLoading) {
      return;
    }

    this.selectedMonthCount.changeValue(value);
    this.setInfoMessage(null);
    this.calculatePrice(tariff);
  };

  pay = async (): Promise<BaseResponse> => {
    if (this.payStage.isLoading) {
      return {
        isError: true
      };
    }

    this.payStage.loading();

    const response = await this._paymentStore.pay(this.paymentParams);

    response.isError ? this.payStage.error() : this.payStage.success();

    return response;
  };

  onPayByCardButtonClick = (): Promise<BaseResponse> => {
    if (this.testPeriodPromoCodeSelected) {
      return this.applyPromoCode();
    }

    this._rootStore.analyticsStore.sendEvent(AnalyticsEvent.paymentBalance, {
      ...this.paymentParams,
      cabinet_origin: window.location.origin
    });

    return this.pay();
  };

  /**
   * Устанавливает значение инпута сумму и сбрасывает выбранные месяцы, info
   * @param value
   * @param withDebounce
   */
  changeInputValueWithReset = (
    value: string,
    withDebounce = true,
    minimalPayment?: number
  ): void => {
    this.amount.changeValue(value);
    this.selectedMonthCount.changeValue(null);
    this.setInfoMessage(null);

    if (!value) {
      return;
    }

    if (withDebounce) {
      this.debouncedValidateInputValue({ minimalPayment });
    } else {
      this.validateInputValue({ minimalPayment });
    }
  };

  changePromocode = ({
    value,
    tariffKind,
    minimalPayment
  }: {
    value: string;
    tariffKind?: TariffKind;
    minimalPayment?: number;
  }): void => {
    this.testPeriodPromoCodeCheckResult.changeValue(null);
    this.setInfoMessage(null);
    this.promoCode.changeValue(value);
    this.debouncedCheckPromocode({ tariffKind, minimalPayment });
  };

  changePaymentAddition = (
    addition: PaymentAddition,
    initialMonthCount?: AvailableMonthCount,
    tariff?: TariffKind
  ): void => {
    this.setInfoMessage(null);
    this.addition.changeValue(addition);
    if (addition === PaymentAddition.sum) {
      const availableSumPayments =
        this._paymentStore.availableSumPayments.value;
      if (availableSumPayments) {
        this.changeInputValueWithReset(String(availableSumPayments[1]), false);
      }
    } else {
      this.selectMonthCount(
        initialMonthCount || availableMonthPayments[2].count,
        tariff
      );
    }
  };

  handleError(code: CalculatePayError | null): void {
    if (code === CalculatePayErrorCode.amountTooLow) {
      if (this._paymentStore.info) {
        this.setInfoMessage({
          isError: true,
          code: CalculatePayErrorCode.amountTooLow,
          data: (t) =>
            t('payment.errors.calculatePay.amount_too_low', {
              ns: 'entities',
              amount: this._paymentStore.info?.minimalPayment,
              currency: this._paymentStore.currency
            })
        });
      }
      return;
    }
    if (code === CalculatePayErrorCode.shouldPayAsLegalEntity) {
      this.setInfoMessage({
        isError: true,
        code: CalculatePayErrorCode.shouldPayAsLegalEntity,
        data: (
          <TypedTransComponent
            i18nKey="payment.errors.should_pay_as_legal_entity_error"
            ns={'entities'}
          >
            ⚠️️ Оплата с&nbsp;карты недоступна, так как подписка оформлена
            на&nbsp;юрлицо. Чтобы пополнить баланс, нажмите «Оплатить по счету»
            и проведите оплату по нему. Если вам нужна помощь по выставлению
            счета - напишите в
            <span
              className="link-new link-new_inherit"
              onClick={this._rootStore.supportPopupStore.openChat}
            >
              техподдержку
            </span>
            .
          </TypedTransComponent>
        )
      });
      return;
    }
    if (
      code &&
      Object.values(CheckPromoCodeError).includes(code as CheckPromoCodeError)
    ) {
      this.promoCode.setError(mapCalculatePayErrorToMessage(code));
    }
    this.setInfoMessage({
      isError: true,
      code: code,
      data: mapErrorCodeToMessage({
        errorCode: code || null,
        map: mapCalculatePayErrorToMessage
      })
    });
  }

  resetAmount = (): void => {
    this.amount.changeValue('');
  };

  resetPromoCode = (): void => {
    this.changePromocode({ value: '' });
  };

  initReactions = (): void => {
    this.addReaction({
      key: 'checkPromoCodeStageValue',
      reaction: reaction(
        () => this.checkPromoCodeStage.value,
        () => {
          this.promoCodeVerified.changeValue(
            this.checkPromoCodeStage.successfullyLoaded
          );
        }
      )
    });
  };
}
